// Dimensional analysis table — Figma "Dimensional Analysis Table" (6384:18389).
//
// A recursive group-by grid: pick up to 3 dimensions (Dim 1 → Dim 2 → Dim 3).
// Rows are grouped by Dim 1; expanding a row re-groups its traffic by the next
// dimension, indented, with the SAME metric columns at every level (Gustavo's
// spec / reference platform). Data comes from the shared dimension layer
// (buildRows + splitRow drill any row down by the next dimension), so metrics
// roll up deterministically and the mock can later be swapped for :9000.
//
// Search, Top 10, expand/collapse and pagination are functional; Filters and
// Columns are presentational selectors for now.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { DotMatrix, FilterPill, Pagination, TableRow } from '@clickguard/design-system'
import { Rows03 } from './icons'
import { ReportSection } from './report-section'
import { SourceIcon } from './source-icon'
import { DimensionPicker } from './dimension-picker'
// The shared table kit (§5.0): the DataTableV2 shell owns the toolbar/header/
// sort/column-reorder/pagination chrome; the props-only parts (GridCell,
// CaretToggle) are rendered by the wiring closures below (renderRow/renderPager)
// which compose the analytics drill-down on top.
import {
	ActiveFilterChip,
	CaretToggle,
	DataTableV2,
	FiltersMenu,
	GridCell,
	reconcileColumnOrder,
	TableEmptyState,
	TableSearchInput,
	useScrollEdges
} from 'components/data-table-v2'
import { usePrefersReducedMotion } from './customize-reveal'
import { useReportLayoutStore } from '../store/use-report-layout-store'
import { useAnalyticsScope, useDimensionCatalog, usePrefetchTable, useTable } from '../data/use-analytics-data'
import {
	badTrafficRate,
	COL_KEY_TO_METRIC_ID,
	liveRowToGridRow,
	RATE_FILTER_TO_API
} from '../data/table-adapters'
import { AnalyticsReport } from 'services/user'
import type { AnalyticsDimensionId, ParentFilter } from 'services/user/analytics-report'
import type { DimensionId, GridRow, MetricColKey } from '../types'
import { ALL_METRIC_COL_KEYS } from '../types'

const fmt = (n: number) => n.toLocaleString()
const fmtUsd = (n: number) => `$${Math.round(n).toLocaleString()}`

// Body rows: 12px / regular per spec (app's text-xs is 10px, so set px explicitly).
const numCell = 'whitespace-nowrap text-[12px] font-normal tabular-nums text-gray-900'

// Bad-traffic-rate cell — the live rate value. A per-row "vs previous period"
// trend chip needs a prev-window figure the /table endpoint doesn't return yet;
// rather than fabricate one, we show the value alone until real per-row growth is
// added (see follow-up). Inner content only (no <TableCell>) so it can be wrapped
// by the shared cell renderer.
const RateCellInner = ({ row }: { row: GridRow }) => (
	<span className={numCell}>{(Math.round(badTrafficRate(row) * 10) / 10).toFixed(1)}%</span>
)

// Toggleable metric columns (the Dimension column is always on). Header label +
// per-row cell live together so the head and body never drift out of sync.
// `render` returns just the cell's CONTENT (not the <td>); <GridCell> wraps it so
// every cell shares the same padding + the nested-row collapse animation.
// `defaultOn` seeds the initial visible set; the click-breakdown columns (valid /
// suspicious / bad) ship hidden and sit right after "Ad clicks analyzed" when a
// user turns them on. Array order = render order. Default-visible reads:
// Ad clicks analyzed · Bad traffic rate · Blocked sources · Prevented ad waste.
// `MetricColKey` is shared from ../types so the layout store can persist the
// visible-column set + order in a view's tableConfig.
// Geography dimensions (country / region / city). Prevented ad waste is a spend
// figure attributed per ad account / campaign — it can't be resolved down to a
// visitor's geography, so a geo row (primary OR nested) shows a muted dash rather
// than a fabricated dollar amount. Keyed on the row's own dimension so a geo
// breakdown nested under a channel dimension dashes too.
const GEO_DIMENSIONS = new Set<DimensionId>(['country', 'region', 'city'])
// Muted em-dash cell for "no data at this granularity".
const WasteDash = () => <span className="text-gray-300" title="Not available by geography">—</span>
// `sortValue` is the numeric the column header sorts by (the rendered cell is a
// formatted string / sparkline, so sorting reads from the raw row instead).
type MetricCol = { key: MetricColKey; label: string; defaultOn: boolean; cellClassName?: string; render: (r: GridRow) => ReactNode; sortValue: (r: GridRow) => number }
const METRIC_COLS: MetricCol[] = [
	{ key: 'analyzed', label: 'Ad clicks analyzed', defaultOn: true, cellClassName: numCell, render: (r) => fmt(r.clicks), sortValue: (r) => r.clicks },
	{ key: 'valid', label: 'Valid clicks', defaultOn: false, cellClassName: numCell, render: (r) => fmt(r.veracity.valid), sortValue: (r) => r.veracity.valid },
	{ key: 'suspicious', label: 'Suspicious clicks', defaultOn: false, cellClassName: numCell, render: (r) => fmt(r.veracity.suspicious), sortValue: (r) => r.veracity.suspicious },
	{ key: 'bad', label: 'Bad clicks', defaultOn: false, cellClassName: numCell, render: (r) => fmt(r.veracity.invalid), sortValue: (r) => r.veracity.invalid },
	{ key: 'rate', label: 'Bad traffic rate', defaultOn: true, render: (r) => <RateCellInner row={r} />, sortValue: (r) => badTrafficRate(r) },
	{ key: 'blocked', label: 'Blocked sources', defaultOn: true, cellClassName: numCell, render: (r) => fmt(r.blockedSources), sortValue: (r) => r.blockedSources },
	// Live rows carry `waste` (null → en-dash per §5.5); mock rows fall back to the
	// derived preventedWaste with the geo-dash rule.
	{ key: 'waste', label: 'Prevented ad waste', defaultOn: true, cellClassName: numCell, render: (r) => typeof r.waste === 'number' ? fmtUsd(r.waste) : <WasteDash />, sortValue: (r) => typeof r.waste === 'number' ? r.waste : -1 }
]

// Reconciling a persisted column order against the known set (drop retired keys,
// append new ones) is the kit's reconcileColumnOrder, called with ALL_METRIC_COL_KEYS.
const reconcileColOrder = (saved: MetricColKey[]): MetricColKey[] =>
	reconcileColumnOrder(saved, [...ALL_METRIC_COL_KEYS])

// ── PDF snapshot (Produces; consumed by the client-side PDF builder) ─────────
// The grid publishes EXACTLY what it renders — post search / filter / sort /
// view, every page (pagination ignored), expanded drill children inline after
// their parent — so the PDF prints the on-screen table instead of re-deriving
// it from the store. A module-level ref written by an effect in the component;
// a later task reads `gridPdfSnapshot.current`.
export interface GridPdfRow {
	label: string
	depth: number
	cells: string[]
}
// Raw per-row values for the PDF's Highlights (findings) — the on-screen cells are
// formatted strings, so the findings read these instead of re-parsing them. Only
// top-level (depth 0) rows, post search/filter/sort, matching `rows`.
export interface GridPdfFindingRow {
	label: string
	clicks: number
	badClicks: number
	badRate: number
}
export interface GridPdfSnapshot {
	dimensionLabel: string
	columns: string[]
	rows: GridPdfRow[]
	/** Optional: absent on older/synthetic snapshots — findings fall back to the
	 *  mock rows when missing. Present for the live grid + the render route. */
	findingRows?: GridPdfFindingRow[]
}
export const gridPdfSnapshot: { current: GridPdfSnapshot | null } = { current: null }

// Textual cell for one metric column — mirrors the column's on-screen render,
// reducing the visual affordances (the bad-rate trend chip, the geo waste dash)
// to their underlying value. The veracity column (a stacked bar on screen)
// prints as its valid / suspicious / bad split, each a whole-percent share of
// the row's clicks, in that order; it isn't a METRIC_COL today, so that guard
// only future-proofs the contract the PDF tasks rely on. The deep_dive control
// column is never a METRIC_COL, so it can't reach here.
const gridPdfCellText = (col: MetricCol, row: GridRow): string => {
	if ((col.key as string) === 'veracity') {
		const pct = (n: number) => (row.clicks ? Math.round((n / row.clicks) * 100) : 0)
		return `${pct(row.veracity.valid)}% · ${pct(row.veracity.suspicious)}% · ${pct(row.veracity.invalid)}%`
	}
	switch (col.key) {
		case 'analyzed':
			return fmt(row.clicks)
		case 'valid':
			return fmt(row.veracity.valid)
		case 'suspicious':
			return fmt(row.veracity.suspicious)
		case 'bad':
			return fmt(row.veracity.invalid)
		case 'rate':
			return `${(Math.round(badTrafficRate(row) * 10) / 10).toFixed(1)}%`
		case 'blocked':
			return fmt(row.blockedSources)
		case 'waste':
			return typeof row.waste === 'number' ? fmtUsd(row.waste) : '—'
		default:
			return ''
	}
}

// Expand caret slide-in/out when a drill dimension is added/removed (the slot
// collapses its width so the row label glides over to fill the gap).
const CARET_T = { duration: 0.26, ease: [0.4, 0, 0.2, 1] } as const
// `!` important on max-w is required for the same reason as ACT_W: the DS TableCell's
// `cg-max-w-0` (ordered after app utilities) otherwise wins and caps the cell back to
// 0, clipping the dimension label once a drill caret + icon widen the content.
const DIM_W = '!w-[300px] !max-w-[300px]'
// Pinned trailing action column. Needs an explicit width: once the cell went
// `sticky`, the column stopped sizing to the Details button and collapsed to just
// its px-6 padding (48px), so the 86px button overflowed left out of the cell.
// 140px = button (~86) + horizontal padding (48) with a hair of slack so the cell
// fully hugs the button. The `!` important modifier is required: the DS TableCell
// ships `cg-max-w-0` (max-width:0) which is ordered AFTER the app utilities and so
// wins a plain `max-w-[140px]` — capping the cell back to its padding and letting
// the button overflow left. `!max-w-[140px]` overrides it.
const ACT_W = '!w-[140px] !max-w-[140px]'
const MotionRow = motion.create(TableRow)

// A drill-down can fan out into hundreds of values (keywords, IPs, URLs…), so a
// nested group with more than this many children paginates IN PLACE — only this
// many child rows render at a time, with a compact in-group pager beneath them.
const NESTED_PAGE_SIZE = 10
// All / Top 10 toolbar toggle hidden per Esteban (2026-07-02); logic stays wired.
const SHOW_VIEW_TOGGLE = false

// Row-level filter presets (operate on the aggregated bad-traffic rate).
type RateFilter = 'all' | 'gt10' | 'gt25'
const RATE_FILTERS: { id: RateFilter; label: string }[] = [
	{ id: 'all', label: 'All rows' },
	{ id: 'gt10', label: 'Bad traffic rate > 10%' },
	{ id: 'gt25', label: 'Bad traffic rate > 25%' }
]

// The Columns / Filters menus and the active-filter chip come from the shared
// data-table-v2 kit (ColumnsMenu is rendered by the DataTableV2 shell itself).

export const DimensionGrid = () => {
	const reduced = usePrefersReducedMotion()
	// Customize mode (read-only here) flips the metric headers into drag handles.
	const editMode = useReportLayoutStore((s) => s.editMode)
	// Report the table's unsaved-edit state up to the header, and listen for the
	// Save/Discard nonces so the table re-baselines or rolls back with everything else.
	const setTableDirty = useReportLayoutStore((s) => s.setTableDirty)
	const tableCommitNonce = useReportLayoutStore((s) => s.tableCommitNonce)
	const tableRevertNonce = useReportLayoutStore((s) => s.tableRevertNonce)
	// The persisted table settings for the active view (part of the committed
	// layout). Local interaction state seeds from this and re-syncs when it changes
	// (view switch / reload); changes flow back via `setTableConfig` so Save writes
	// them into the view's config. `committedTableConfig` is a fresh object per
	// view apply, so watching its identity re-inits the grid on view switches.
	const setTableConfig = useReportLayoutStore((s) => s.setTableConfig)
	const committedTableConfig = useReportLayoutStore((s) => s.committed.tableConfig)
	const sectionTitle = useReportLayoutStore((s) => s.sectionNames.dimension)
	const setSectionName = useReportLayoutStore((s) => s.setSectionName)
	const [search, setSearch] = useState('')
	// The input updates `search` immediately (responsive field), but the table
	// query keys off this debounced copy: every distinct key is a fresh /table
	// fetch (~6 BigQuery queries), so querying on each keystroke would bill the
	// warehouse for every intermediate prefix of what the user types.
	const [debouncedSearch, setDebouncedSearch] = useState('')
	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search), 300)
		return () => clearTimeout(t)
	}, [search])
	const [view, setView] = useState<'all' | 'top10'>('all')
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	// Dim 1 is required; Dim 2 / Dim 3 are optional drill-downs (clearable). Seed
	// from the active view's persisted tableConfig (factory default = a flat Dim-1
	// list, no drill dimensions, until the user adds one).
	const [dim1, setDim1] = useState<DimensionId>(() => committedTableConfig.dim1)
	const [dim2, setDim2] = useState<DimensionId | null>(() => committedTableConfig.dim2)
	const [dim3, setDim3] = useState<DimensionId | null>(() => committedTableConfig.dim3)
	const [expanded, setExpanded] = useState<Set<string>>(new Set())
	// Per-group current page (keyed by the parent row's id) for nested groups that
	// exceed NESTED_PAGE_SIZE. Cleared whenever the dimension chain changes.
	const [nestedPage, setNestedPage] = useState<Record<string, number>>({})
	const setGroupPage = (id: string, p: number) => setNestedPage((prev) => ({ ...prev, [id]: p }))
	// Per-group rows-per-page, so each nested pager carries its own picker like the
	// footer. Defaults to NESTED_PAGE_SIZE until that group's picker changes it.
	const [nestedSize, setNestedSize] = useState<Record<string, number>>({})
	const setGroupSize = (id: string, s: number) => setNestedSize((prev) => ({ ...prev, [id]: s }))
	// Visible metric columns + their display order (Dimension stays pinned first),
	// seeded from the view's persisted tableConfig and reconciled against the known
	// column set (drops retired keys, appends newly-added ones). Reordered by
	// dragging the headers in Customize mode; mirrored back to the store on change
	// so Save persists them into the view's config.
	const [visibleCols, setVisibleCols] = useState<Set<MetricColKey>>(
		() => new Set<MetricColKey>(committedTableConfig.visibleColumns)
	)
	const [colOrder, setColOrder] = useState<MetricColKey[]>(
		() => reconcileColOrder(committedTableConfig.columnOrder)
	)

	// ── Save/Discard coordination ──────────────────────────────────────────────
	// The table's saveable settings (dimensions + which metric columns show, and
	// their order) live in local state and are mirrored into the store's draft
	// tableConfig (below) so Save persists them and `selectDirty` sees them. We ALSO
	// track a local baseline (the "last save") to report divergence as `tableDirty`
	// and to roll back on Discard — re-baselining on Save and reverting on Discard
	// when their nonces bump.
	const tableBaseline = useRef({
		dim1,
		dim2,
		dim3,
		cols: [...visibleCols],
		order: [...colOrder]
	})
	useEffect(() => {
		const b = tableBaseline.current
		const cols = [...visibleCols]
		const sameCols =
			cols.length === b.cols.length && cols.every((c) => b.cols.includes(c))
		const sameOrder =
			colOrder.length === b.order.length &&
			colOrder.every((c, i) => c === b.order[i])
		setTableDirty(
			dim1 !== b.dim1 || dim2 !== b.dim2 || dim3 !== b.dim3 || !sameCols || !sameOrder
		)
	}, [dim1, dim2, dim3, visibleCols, colOrder, setTableDirty])
	// Mirror the current settings into the store's draft tableConfig so Save writes
	// them into the view's config and structural dirty detection picks them up.
	// `visibleColumns` is serialized in columnOrder order (canonical) so the stored
	// value is stable; setTableConfig no-ops when nothing actually changed.
	useEffect(() => {
		setTableConfig({
			dim1,
			dim2,
			dim3,
			columnOrder: [...colOrder],
			visibleColumns: colOrder.filter((k) => visibleCols.has(k))
		})
	}, [dim1, dim2, dim3, visibleCols, colOrder, setTableConfig])
	// Save → re-baseline the table to its current settings (skip the mount fire).
	const didCommitMount = useRef(false)
	useEffect(() => {
		if (!didCommitMount.current) {
			didCommitMount.current = true
			return
		}
		tableBaseline.current = { dim1, dim2, dim3, cols: [...visibleCols], order: [...colOrder] }
		setTableDirty(false)
		// eslint-disable-next-line
	}, [tableCommitNonce])
	// Discard → roll the table's settings back to the baseline (skip the mount fire).
	const didRevertMount = useRef(false)
	useEffect(() => {
		if (!didRevertMount.current) {
			didRevertMount.current = true
			return
		}
		const b = tableBaseline.current
		setDim1(b.dim1)
		setDim2(b.dim2)
		setDim3(b.dim3)
		setVisibleCols(new Set(b.cols))
		setColOrder([...b.order])
		setTableDirty(false)
		// eslint-disable-next-line
	}, [tableRevertNonce])
	// View switch / reload → adopt the newly-applied view's persisted table settings
	// and re-baseline. `committedTableConfig` is a fresh object per applyView, so its
	// identity change drives this. Skips when it already matches local state (our own
	// mirrored write echoing back, the initial mount, or a Save that just committed
	// what's on screen), so it never clobbers an in-progress edit.
	useEffect(() => {
		const c = committedTableConfig
		const localVisible = colOrder.filter((k) => visibleCols.has(k))
		const same =
			c.dim1 === dim1 &&
			c.dim2 === dim2 &&
			c.dim3 === dim3 &&
			c.columnOrder.length === colOrder.length &&
			c.columnOrder.every((k, i) => k === colOrder[i]) &&
			c.visibleColumns.length === localVisible.length &&
			c.visibleColumns.every((k, i) => k === localVisible[i])
		if (same) return
		const order = reconcileColOrder(c.columnOrder)
		setDim1(c.dim1)
		setDim2(c.dim2)
		setDim3(c.dim3)
		setColOrder(order)
		setVisibleCols(new Set(c.visibleColumns))
		// A sort on a column the view hides would stay applied with no header to
		// clear it from — drop it.
		setSort((s) => (s && s.key !== 'dimension' && !c.visibleColumns.includes(s.key) ? null : s))
		tableBaseline.current = {
			dim1: c.dim1,
			dim2: c.dim2,
			dim3: c.dim3,
			cols: [...c.visibleColumns],
			order
		}
		setTableDirty(false)
		// eslint-disable-next-line
	}, [committedTableConfig])

	const [rateFilter, setRateFilter] = useState<RateFilter>('all')
	// Column sort. `null` = natural order. Clicking a header cycles desc → asc →
	// off; numerics sort by the column's `sortValue`, the Dimension column by label.
	const [sort, setSort] = useState<{ key: MetricColKey | 'dimension'; dir: 'asc' | 'desc' } | null>(null)
	// Horizontal-scroll bookkeeping for the pinned Dimension column — the kit hook
	// tracks whether content is hidden past either edge (gates the drop shadows),
	// re-measuring on scroll/resize and when the visible column set changes width.
	const { scrollRef, edge, measure } = useScrollEdges([visibleCols, colOrder])
	const toggleCol = (k: MetricColKey) => {
		// Hiding the sorted column: clear the sort too, or it stays applied with no
		// header to see or clear it from.
		if (visibleCols.has(k)) setSort((s) => (s && s.key === k ? null : s))
		setVisibleCols((prev) => {
			const next = new Set(prev)
			next.has(k) ? next.delete(k) : next.add(k)
			return next
		})
	}
	// Visible columns in display order — the body cells render from this; the
	// DataTableV2 shell derives the same list for the header (and owns the
	// Customize-mode header drag-to-reorder, writing back via setColOrder).
	const cols = colOrder.map((k) => METRIC_COLS.find((c) => c.key === k)!).filter((c) => visibleCols.has(c.key))

	// The active dimension chain. Dim 3 only counts when Dim 2 is set (no holes).
	const dims = useMemo<DimensionId[]>(() => {
		const d: DimensionId[] = [dim1]
		if (dim2) {
			d.push(dim2)
			if (dim3) d.push(dim3)
		}
		return d
	}, [dim1, dim2, dim3])

	// Any dimension change re-keys every row → drop expand state + reset paging.
	const reset = () => {
		setExpanded(new Set())
		setNestedPage({})
		setNestedSize({})
		setPage(1)
	}
	const changeDim1 = (id: DimensionId | null) => {
		if (id) setDim1(id)
		reset()
	}
	const changeDim2 = (id: DimensionId | null) => {
		setDim2(id)
		if (!id) setDim3(null)
		reset()
	}
	// Dim 3 is the DEEPEST level: adding / swapping / clearing it never changes any
	// dim1 or dim2 row id (those are built from the shallower chain, and dim3 rows are
	// leaves that are never themselves expanded), so the expand / breakdown / paging
	// state stays valid. Keep it intact instead of resetting — the table stays open and
	// the new bottom level slides in via the per-row carets + AnimatePresence, rather
	// than collapsing the whole tree. (Stale nested page numbers are clamped in `walk`,
	// and a now-leaf row left in `expanded` renders closed since `canExpand` is false.)
	const changeDim3 = (id: DimensionId | null) => {
		setDim3(id)
	}

	// Live top-level (Dim 1) rows — search / filter / sort / pagination are all
	// server-side (aggregation engine). `view === 'top10'` maps to a bad-rate sort;
	// dimension-label sort isn't server-supported yet, so it falls back to default.
	const scope = useAnalyticsScope()
	const scopeSig = scope.key.join('|')
	// Server label for the dimension the PDF snapshot names, matching the picker.
	const { labelFor: dimensionLabelFor } = useDimensionCatalog()
	const sortMetric =
		view === 'top10'
			? 'bad_traffic_rate'
			: sort && sort.key !== 'dimension'
			? COL_KEY_TO_METRIC_ID[sort.key]
			: undefined
	const sortDir: 'asc' | 'desc' = view === 'top10' ? 'desc' : sort?.dir ?? 'desc'
	const liveTable = useTable({
		// dim ids come from the picker, which only offers valid analytics
		// dimensions; DimensionId is the prototype's loose string alias.
		dimension: dim1 as AnalyticsDimensionId,
		filter: RATE_FILTER_TO_API[rateFilter],
		sortMetric,
		sortDir,
		search: debouncedSearch.trim() || undefined,
		page: page - 1,
		pageSize
	})
	// Background-warm a dimension's landing view (its top level at page 1 with the
	// current sort/filter/search, since a dim switch keeps those). Called by the
	// primary picker for the dimensions the user is browsing, so the switch is
	// instant instead of a fresh load.
	const prefetchTable = usePrefetchTable()
	const prefetchDimension = useCallback(
		(dimId: DimensionId) =>
			prefetchTable({
				dimension: dimId as AnalyticsDimensionId,
				filter: RATE_FILTER_TO_API[rateFilter],
				sortMetric,
				sortDir,
				search: debouncedSearch.trim() || undefined,
				page: 0,
				pageSize
			}),
		[prefetchTable, rateFilter, sortMetric, sortDir, debouncedSearch, pageSize]
	)

	// Which dim1 the currently-held rows belong to. keepPreviousData keeps the old
	// rows visible across a refetch — right for sort/page/filter (same dimension),
	// but on a DIMENSION change those stale rows get re-tagged with the new dim's
	// id, so their icons flip to the new dimension while the values are still the
	// old one. Track the data's dimension and blank the rows while a *new*
	// dimension loads, so the user sees a clean loading state, not a mismatch.
	const dataDimRef = useRef(dim1)
	useEffect(() => {
		if (liveTable.data && !liveTable.isPreviousData) dataDimRef.current = dim1
	}, [liveTable.data, liveTable.isPreviousData, dim1])
	// Blank ONLY while react-query is showing the previous query's data (keepPrevious)
	// AND that data belongs to a different dimension. Using isPreviousData (not
	// isFetching) means a cached/prefetched dimension shows instantly instead of
	// flashing the loader, while sort/page/filter refetches (same dim) keep-previous.
	const dimensionReloading = liveTable.isPreviousData && dataDimRef.current !== dim1
	const pageRows = useMemo<GridRow[]>(
		() =>
			dimensionReloading
				? []
				: (liveTable.data?.rows ?? []).map((r) => liveRowToGridRow(r, dim1)),
		[liveTable.data, dim1, dimensionReloading]
	)
	const totalRows = dimensionReloading ? 0 : liveTable.data?.total ?? 0
	const totalPages = Math.max(1, Math.ceil(totalRows / pageSize))
	const safePage = Math.min(page, totalPages)

	// Lazy drill-down: children per expanded node, fetched on expand and cached by
	// the node's flat key. `pageSize: 100` covers typical child counts; the nested
	// pager still shows 10/page. (Groups >100 children are capped — a follow-up
	// would paginate nested groups server-side.)
	const [childrenCache, setChildrenCache] = useState<Record<string, GridRow[]>>({})
	// Cache key = the node's flat key + the CHILD dimension. Keying by dimension
	// means swapping the third dimension can't serve a row's old children under
	// the new one, and a parent's (second-dimension) children stay valid.
	const childKey = (key: string, dim: DimensionId) => `${key}|${dim}`
	// Nodes whose drill children are currently being fetched — drives the inline
	// "loading children" row while an expand is in flight.
	const [loadingChildren, setLoadingChildren] = useState<Set<string>>(new Set())
	// Nodes whose last drill fetch failed — rendered as an inline "couldn't load /
	// Retry" row (not just a toast), and excluded from the auto-refetch so a
	// persistent error can't loop (Cherry #0005).
	const [failedChildren, setFailedChildren] = useState<Set<string>>(new Set())
	// Invalidate cached children when the row identity or scope/date changes. Not
	// on dim3: those rows' ids and second-dimension children are unaffected (the
	// cache is keyed by child dimension, so a new third dimension simply misses).
	useEffect(() => {
		setChildrenCache({})
	}, [dim1, dim2, rateFilter, scopeSig])
	const fetchChildren = useCallback(
		async (
			cacheKey: string,
			parentPath: { dimension: DimensionId; value: string }[],
			childDim: DimensionId
		) => {
			if (!scope.ready) return
			setLoadingChildren((prev) => new Set(prev).add(cacheKey))
			setFailedChildren((prev) => {
				if (!prev.has(cacheKey)) return prev
				const next = new Set(prev)
				next.delete(cacheKey)
				return next
			})
			try {
				const res = await AnalyticsReport.getTable(
					scope.accountId as string,
					scope.assetId as string,
					{
						...scope.body,
						dimension: childDim as AnalyticsDimensionId,
						parentPath: parentPath as ParentFilter[],
						pageSize: 100
					}
				)
				setChildrenCache((prev) =>
					prev[cacheKey]
						? prev
						: {
								...prev,
								[cacheKey]: res.rows.map((r) =>
									liveRowToGridRow(r, childDim, cacheKey)
								)
						  }
				)
			} catch {
				// Nothing is cached; the row shows an inline error with a Retry
				// (see walk / renderNote) rather than a silent blank or a toast.
				setFailedChildren((prev) => new Set(prev).add(cacheKey))
			} finally {
				setLoadingChildren((prev) => {
					const next = new Set(prev)
					next.delete(cacheKey)
					return next
				})
			}
		},
		[scope.ready, scope.accountId, scope.assetId, scope.body]
	)

	// Publish the rendered table for the PDF: the on-screen page rows with any
	// expanded (cached) drill children flattened inline at one greater depth.
	// Columns/cell text mirror the visible metric columns. Cleared on unmount.
	useEffect(() => {
		const snapCols = colOrder
			.map((k) => METRIC_COLS.find((c) => c.key === k)!)
			.filter((c) => visibleCols.has(c.key))
		const rows: GridPdfRow[] = []
		const walkPdf = (row: GridRow, depth: number, key: string) => {
			rows.push({
				label: row.label,
				depth,
				cells: snapCols.map((c) => gridPdfCellText(c, row))
			})
			const nextDim = dims[depth + 1]
			if (!nextDim || !expanded.has(row.id)) return
			;(childrenCache[childKey(key, nextDim)] ?? []).forEach((child, i) =>
				walkPdf(child, depth + 1, `${key}:${i}`)
			)
		}
		pageRows.forEach((r) => walkPdf(r, 0, r.id))
		gridPdfSnapshot.current = {
			dimensionLabel: dimensionLabelFor(dim1),
			columns: snapCols.map((c) => c.label),
			rows,
			// Raw top-level values so the PDF Highlights quote real numbers.
			findingRows: pageRows.map((r) => ({
				label: r.label,
				clicks: r.clicks,
				badClicks: r.veracity.invalid,
				badRate: badTrafficRate(r)
			}))
		}
		return () => {
			gridPdfSnapshot.current = null
		}
	}, [pageRows, dims, expanded, visibleCols, colOrder, dim1, childrenCache, dimensionLabelFor])

	const toggle = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev)
			next.has(id) ? next.delete(id) : next.add(id)
			return next
		})

	const setViewReset = (v: 'all' | 'top10') => {
		setView(v)
		setPage(1)
	}

	// Nesting is conveyed by depth background shading (the expanded subtree reads
	// as a soft band, deeper = darker) plus a brand-colored line closing the
	// block where the nesting ends (i.e. just before the table returns to a
	// top-level row). Flatten the visible rows first so we can see that boundary.
	//
	// A nested group with more than NESTED_PAGE_SIZE children paginates in place:
	// only the current page's children are walked, followed by a `pager` item.
	// Child keys are POSITIONAL (`${parentKey}:${slotIndex}`) so paging a group
	// swaps row content in stable slots instead of unmounting/remounting — no
	// collapse-and-regrow flash on next/prev; only expand/collapse animates.
	const INDENT = 24
	// Drill-down child fetches allowed in flight at once (each is ~3 BigQuery jobs).
	const MAX_CONCURRENT_DRILL_FETCHES = 4
	const SHADE = ['', 'var(--pa-row-shade-1)', 'var(--pa-row-shade-2)', 'var(--pa-row-shade-3)'] // depth 0 = none (card surface); softer per level, theme-aware (App.css)
	type FlatRow = {
		kind: 'row'
		key: string
		row: GridRow
		depth: number
		// Ancestor (dimension, value) chain — used to scope this row's lazy child fetch.
		parentPath: { dimension: DimensionId; value: string }[]
	}
	type FlatPager = { kind: 'pager'; key: string; parentId: string; depth: number; page: number; totalPages: number; total: number; from: number; to: number; pageSize: number }
	type FlatLoading = { kind: 'loading'; key: string; depth: number }
	// A drill-down that returned NO rows — distinct from a failure (Cherry #0005:
	// "if the issue is that there's no data the error should be different").
	type FlatEmpty = { kind: 'empty'; key: string; depth: number; dimension: DimensionId }
	type FlatError = { kind: 'error'; key: string; depth: number; retry: () => void }
	type FlatItem = FlatRow | FlatPager | FlatLoading | FlatEmpty | FlatError
	const flat: FlatItem[] = []
	const walk = (
		row: GridRow,
		depth: number,
		key: string,
		parentPath: { dimension: DimensionId; value: string }[]
	) => {
		flat.push({ kind: 'row', key, row, depth, parentPath })
		// Reason breakdown panel sits directly under its row (before any dimension
		// children), full-width, at the row's own depth.
		const nextDim = dims[depth + 1]
		if (!nextDim || !expanded.has(row.id)) return
		// Children come from the lazy cache (fetched on expand, keyed by this node's
		// flat key). Empty while the fetch is in flight.
		const ck = childKey(key, nextDim)
		const children = childrenCache[ck] ?? []
		const cd = depth + 1
		// Path passed to each child's own lazy fetch: this row's ancestry + itself.
		const childPath = [
			...parentPath,
			{ dimension: row.dimensionId, value: row.valueId }
		]
		// Drill fetch still in flight for this node → show a loading row (§ drill
		// takes a moment on deep dimensions) instead of a silent gap.
		if (children.length === 0 && loadingChildren.has(ck)) {
			flat.push({ kind: 'loading', key: `${key}:loading`, depth: cd })
			return
		}
		// Failed fetch (nothing cached): inline error + Retry, at the child depth.
		if (!childrenCache[ck] && failedChildren.has(ck)) {
			flat.push({ kind: 'error', key: `${key}:error`, depth: cd, retry: () => fetchChildren(ck, childPath, nextDim) })
			return
		}
		// Fetched, genuinely empty (the parent's hasChildren is depth-based; e.g. a
		// campaign whose clicks carry no keyword): say so instead of a silent gap.
		if (childrenCache[ck] && children.length === 0) {
			flat.push({ kind: 'empty', key: `${key}:empty`, depth: cd, dimension: nextDim })
			return
		}
		// Gate on the DEFAULT page size (10): a group with more than that gets a
		// pager, which STAYS even if its picker later raises the size so all fit on
		// one page (otherwise the size control would vanish and you couldn't lower it).
		if (children.length > NESTED_PAGE_SIZE) {
			const size = nestedSize[row.id] ?? NESTED_PAGE_SIZE
			const totalPages = Math.ceil(children.length / size)
			const pg = Math.min(nestedPage[row.id] ?? 1, totalPages)
			const start = (pg - 1) * size
			const slice = children.slice(start, start + size)
			slice.forEach((child, i) => walk(child, cd, `${key}:${i}`, childPath))
			flat.push({ kind: 'pager', key: `${key}:pager`, parentId: row.id, depth: cd, page: pg, totalPages, total: children.length, from: start + 1, to: start + slice.length, pageSize: size })
		} else {
			children.forEach((child, i) => walk(child, cd, `${key}:${i}`, childPath))
		}
	}
	pageRows.forEach((r) => walk(r, 0, r.id, []))

	// Open rows whose children aren't cached — a third dimension was added or
	// swapped, or a scope / date / filter change cleared the cache. Fetch them so
	// an open caret always shows rows (or the loading row) rather than staying
	// open over nothing (Cherry #0006: "either load the row or close it").
	// In-flight nodes are skipped; the signature keeps the effect to one pass per gap.
	const missingChildren = flat.flatMap((it) => {
		if (it.kind !== 'row') return []
		const nextDim = dims[it.depth + 1]
		if (!nextDim || !it.row.hasChildren || !expanded.has(it.row.id)) return []
		const ck = childKey(it.key, nextDim)
		if (childrenCache[ck] || loadingChildren.has(ck) || failedChildren.has(ck)) return []
		return [{ ck, path: [...it.parentPath, { dimension: it.row.dimensionId, value: it.row.valueId }], nextDim }]
	})
	const missingSig = missingChildren.map((m) => m.ck).join('\n')
	// Bounded fan-out: a date / scope change with many rows open would otherwise
	// fire one getTable per open row in a single tick (three BigQuery jobs each).
	// Fill up to the cap; as fetches land the cache changes, the signature
	// changes and the effect refills the window with the next rows.
	const inFlight = loadingChildren.size
	useEffect(() => {
		if (!missingSig) return
		missingChildren
			.slice(0, Math.max(0, MAX_CONCURRENT_DRILL_FETCHES - inFlight))
			.forEach((m) => fetchChildren(m.ck, m.path, m.nextDim))
	}, [missingSig, inFlight, fetchChildren])

	// End of a nested block at ANY depth: this item is nested and the next visible
	// item steps back out (shallower) → close it with the brand line. Works for
	// both row and pager items (the pager, when present, is the block's last item).
	const endsNestAt = (i: number, depth: number) => depth > 0 && i < flat.length - 1 && flat[i + 1].depth < depth

	const renderRow = (
		{ row, depth, key, parentPath }: FlatRow,
		i: number
	): ReactNode => {
		const nextDim = dims[depth + 1]
		// A caret needs actual children for the next dimension — the live engine
		// reports this per row via hasChildren (a leaf with no sub-values doesn't
		// expand into nothing).
		const canExpand = !!nextDim && !!row.hasChildren
		const isOpen = canExpand && expanded.has(row.id)
		// Expand toggles the row and, on open, lazily fetches its children scoped to
		// this row's ancestry (cached by the flat key).
		const onExpandToggle = () => {
			if (canExpand && nextDim && !expanded.has(row.id)) {
				fetchChildren(
					childKey(key, nextDim),
					[...parentPath, { dimension: row.dimensionId, value: row.valueId }],
					nextDim
				)
			}
			toggle(row.id)
		}
		const endsNest = endsNestAt(i, depth)
		// Only nested rows (depth > 0) animate in/out; top-level rows are paginated
		// and always present. A motion row lets <AnimatePresence> hold it mounted
		// through the collapse so the close animates too.
		const animate = depth > 0 && !reduced
		const RowEl = (animate ? MotionRow : TableRow) as typeof TableRow
		// Reserve an empty caret slot only when SOME level can drill (so leaf rows stay
		// aligned under their expandable ancestors). A fully flat list (no drill dim)
		// reserves nothing, so the source icon sits flush left.
		const hasDrill = dims.length > 1
		const dimContent = (
			// min-h pins the cell to the caret button's own height (cg-size-6 = 24px, taller
			// than the 20px source icon) for EVERY row, so a row never grows/shrinks as its
			// caret appears or disappears — the old layout let the 24px button drive height and
			// the row collapsed to 20px the instant it left.
			<div className="flex min-h-[24px] min-w-0 items-center gap-2" style={{ paddingLeft: depth * INDENT }}>
				{/* Caret column — two always-mounted layers driven by plain `animate` props (NOT
				    AnimatePresence), so when a dimension is added/cleared both tweens run at once
				    instead of the parent waiting for the child's exit (that sequencing was the
				    "slides off, then the column slides after" glitch):
				    • OUTER width/margin track hasDrill: the 24px column slides open/closed when the
				      table gains/loses ALL drilling, so every row shifts together, not popping.
				    • INNER opacity/slide track canExpand: within a column that stays put, the caret
				      fades + slides as one row gains/loses its own drill (e.g. clearing dim3) with
				      no width reflow — the column holds 24px, so the row never bounces.
				    The caret stays in the DOM at opacity 0 on non-drillable rows (pointer-events
				    off so it can't be clicked); min-h-[24px] above pins the row to the mini button's
				    height (cg-size-6 = 24px > the 20px icon) so it can't shrink when the caret hides. */}
				<motion.span
					className="inline-flex h-6 shrink-0 items-center justify-center overflow-hidden"
					initial={false}
					animate={{ width: hasDrill ? 24 : 0, marginRight: hasDrill ? 12 : 0 }}
					transition={reduced ? { duration: 0 } : CARET_T}
				>
					<motion.span
						className="inline-flex"
						aria-hidden={!canExpand}
						style={{ pointerEvents: canExpand ? 'auto' : 'none' }}
						initial={false}
						animate={{ opacity: canExpand ? 1 : 0, x: canExpand ? 0 : -6 }}
						transition={reduced ? { duration: 0 } : CARET_T}
					>
						<CaretToggle expanded={isOpen} onToggle={onExpandToggle} label={`Expand ${row.label}`} />
					</motion.span>
				</motion.span>
				<SourceIcon dimensionId={row.dimensionId} valueId={row.valueId} iconMeta={row.iconMeta} className="h-5 w-5 shrink-0" />
				<span className="min-w-0 truncate text-[12px] font-normal text-[rgb(var(--cg-text-primary))]" title={row.label}>
					{row.label}
				</span>
			</div>
		)
		return (
			<RowEl
				key={key}
				onClick={canExpand ? onExpandToggle : undefined}
				// Nested rows drop the DS per-row gray divider: nesting is shown by the
				// shaded band + the brand closing line, and those 1px dividers otherwise
				// STACK into a gray band while a group collapses. Top-level rows keep theirs.
				// `group` only on depth-0 rows — they're the ones the DS greys on hover, so
				// the sticky cells' hover overlay must track exactly those (nested rows carry
				// an inline shade bg that already suppresses the DS hover).
				className={[depth === 0 ? 'group' : '', canExpand ? 'cursor-pointer' : '', animate ? '!border-b-0' : ''].filter(Boolean).join(' ') || undefined}
				style={depth > 0 ? { backgroundColor: SHADE[Math.min(depth, SHADE.length - 1)] } : undefined}
			>
				<GridCell
					animate={animate}
					width={DIM_W}
					lineClose={endsNest}
					sticky
					scrolled={!edge.start}
					rowDivider={depth === 0}
					bg={depth > 0 ? SHADE[Math.min(depth, SHADE.length - 1)] : 'var(--background)'}
				>
					{dimContent}
				</GridCell>
				{cols.map((c) => (
					<GridCell key={c.key} animate={animate} className={c.cellClassName} lineClose={endsNest}>
						{c.render(row)}
					</GridCell>
				))}
				{/* §5.8 "Details" (invalid-traffic threat breakdown) is OUT OF SCOPE for
				    v1 (depends on the threat-taxonomy project), so its trailing action
				    column is not rendered — it would only show placeholder data. The
				    table's flat-item render pattern still supports adding a per-row
				    expandable region later without restructuring ("engineer for it"). */}
			</RowEl>
		)
	}

	const renderPager = (item: FlatPager, i: number): ReactNode => {
		const animate = !reduced // pagers only exist inside nested (depth > 0) groups
		const RowEl = (animate ? MotionRow : TableRow) as typeof TableRow
		return (
			<RowEl key={item.key} className={animate ? '!border-b-0' : undefined} style={{ backgroundColor: SHADE[Math.min(item.depth, SHADE.length - 1)] }}>
				<GridCell animate={animate} colSpan={1 + cols.length} lineClose={endsNestAt(i, item.depth)} className="!py-2.5">
					{/* Canonical DS Pagination — configured identically to the table footer:
					    rows-per-page picker + first/last, per-group state. Indented to the
					    group's depth; clicks don't toggle the row. */}
					<div style={{ paddingLeft: item.depth * INDENT }} onClick={(e) => e.stopPropagation()}>
						<Pagination
							page={item.page}
							totalPages={item.totalPages}
							onPageChange={(p) => setGroupPage(item.parentId, p)}
							pageSize={item.pageSize}
							pageSizeOptions={[10, 25, 50]}
							onPageSizeChange={(s) => {
								setGroupSize(item.parentId, s)
								setGroupPage(item.parentId, 1)
							}}
							showSummary
							showFirstLast
						/>
					</div>
				</GridCell>
			</RowEl>
		)
	}

	// Full-width inline row shown while a drill-down node's children are being
	// fetched, so a deep dimension expand isn't a silent gap.
	const renderLoading = (item: FlatLoading, i: number): ReactNode => {
		const animate = !reduced
		const RowEl = (animate ? MotionRow : TableRow) as typeof TableRow
		return (
			<RowEl key={item.key} className="hover:!bg-transparent" style={{ backgroundColor: SHADE[Math.min(item.depth, SHADE.length - 1)] }}>
				<GridCell animate={animate} colSpan={1 + cols.length} lineClose={endsNestAt(i, item.depth)} className="!py-3">
					<div className="flex items-center gap-2 text-[13px] text-gray-500" style={{ paddingLeft: item.depth * INDENT }}>
						{/* DS DotMatrix, the house loader, not the spinner (Cherry #0007). */}
						<DotMatrix size="xs" label="Loading rows" />
						Loading…
					</div>
				</GridCell>
			</RowEl>
		)
	}

	// Inline note rows for a drill-down node: no rows (plain), or a failure with a
	// Retry action. Same shell as the loading row.
	const renderNote = (item: FlatEmpty | FlatError, i: number): ReactNode => {
		const animate = !reduced
		const RowEl = (animate ? MotionRow : TableRow) as typeof TableRow
		return (
			<RowEl key={item.key} className="hover:!bg-transparent" style={{ backgroundColor: SHADE[Math.min(item.depth, SHADE.length - 1)] }}>
				<GridCell animate={animate} colSpan={1 + cols.length} lineClose={endsNestAt(i, item.depth)} className="!py-3">
					<div className="flex items-center gap-2 text-[13px] text-gray-500" style={{ paddingLeft: item.depth * INDENT }}>
						{item.kind === 'empty' ? (
							<>No {dimensionLabelFor(item.dimension).toLowerCase()} rows for this selection</>
						) : (
							<>
								Couldn’t load this breakdown.
								<button
									type="button"
									onClick={item.retry}
									className="font-medium text-[rgb(var(--cg-brand))] transition-colors hover:text-[#443ac9]"
								>
									Retry
								</button>
							</>
						)}
					</div>
				</GridCell>
			</RowEl>
		)
	}

	const renderItem = (item: FlatItem, i: number): ReactNode =>
		item.kind === 'pager'
			? renderPager(item, i)
			: item.kind === 'loading'
			? renderLoading(item, i)
			: item.kind === 'empty' || item.kind === 'error'
			? renderNote(item, i)
			: renderRow(item, i)

	const headerSearch = (
		// Figma "Search" (node 6526:40721 › 6269:11771): the box border is
		// border/default #e1e1e1 — softer than the DS Input's default
		// cg-border-border-strong (#c8c8c8). The DS Input nests the bordered box
		// inside a label/field stack, so target the grandchild box (myDiv > stack
		// > box). Override only the resting border via the app's own Tailwind,
		// keeping the DS brand focus ring on focus-within.
		// Resting width spans from the Top 10 button's RIGHT edge to the card's right
		// edge — the Columns button width (128px) + the 8px inter-button gap = 136px
		// — so the resting field reads as one continuous line flush with the toolbar
		// below. On focus it eases further left to where Top 10 STARTS (200px).
		// (Widths align to the toolbar buttons below — see canvas-header.)
		<TableSearchInput
			value={search}
			onChange={(next) => {
				setSearch(next)
				setPage(1)
			}}
		/>
	)

	return (
		<ReportSection
			title={sectionTitle}
			onTitleChange={(next) => setSectionName('dimension', next)}
			headerAction={headerSearch}
		>
			{/* White card content: 12px padding, 16px gap between controls and grid
			    (Figma node I6526:40721;6269:11772 — p-12, gap-16). The shared
			    DataTableV2 shell renders the toolbar (with the pickers/filters in its
			    leading slot), the sortable/reorderable header, the body shell, the
			    empty/loading states and the pagination footer; the analytics-specific
			    rows (drill-down, in-place pagers) come in as children via renderItem.
			    Top-level row swaps are INSTANT (never remounted/faded — a keyed fade
			    here used to read as a blank-then-fade flash); the per-row drill
			    expand/collapse still animates inside the body's AnimatePresence. */}
			<div className="flex flex-col gap-4 p-3">
				<DataTableV2<GridRow, MetricColKey>
					columns={METRIC_COLS}
					columnOrder={colOrder}
					onColumnOrderChange={setColOrder}
					visibleColumns={visibleCols}
					onToggleColumn={toggleCol}
					leading={{ label: 'Dimension', className: DIM_W, sortKey: 'dimension' }}
					sort={sort}
					onSortChange={(next) => {
						setSort(next as { key: MetricColKey | 'dimension'; dir: 'asc' | 'desc' } | null)
						setPage(1)
					}}
					editMode={editMode}
					toolbarLeading={
						<>
							<DimensionPicker value={dim1} onChange={changeDim1} icon={<Rows03 className="h-4 w-4" />} exclude={[dim2, dim3]} onPrefetch={prefetchDimension} />
							<DimensionPicker value={dim2} onChange={changeDim2} allowNone addLabel="Add dimension 2" exclude={[dim1, dim3]} />
							<DimensionPicker value={dim3} onChange={changeDim3} allowNone addLabel="Add dimension 3" exclude={[dim1, dim2]} />
							<FiltersMenu
								options={RATE_FILTERS}
								value={rateFilter}
								onChange={(v) => {
									setRateFilter(v)
									setPage(1)
								}}
							/>
							{/* Removable chip surfacing the active filter (Figma 5345:181113). */}
							<AnimatePresence initial={false}>
								{rateFilter !== 'all' && (
									<ActiveFilterChip
										key="rate-chip"
										field="Bad traffic rate"
										value={rateFilter === 'gt10' ? '> 10%' : '> 25%'}
										reduced={reduced}
										onClear={() => {
											setRateFilter('all')
											setPage(1)
										}}
									/>
								)}
							</AnimatePresence>
						</>
					}
					toolbarTrailing={
						/* All / Top 10 view toggle hidden per Esteban (2026-07-02) — flip the
						   flag to bring it back; the view state + Top 10 sorting logic stay wired. */
						SHOW_VIEW_TOGGLE ? (
							<>
								<FilterPill label="All" pressed={view === 'all'} showCheck={view === 'all'} onPressedChange={() => setViewReset('all')} />
								<FilterPill label="Top 10" pressed={view === 'top10'} showCheck={view === 'top10'} onPressedChange={() => setViewReset('top10')} />
							</>
						) : undefined
					}
					scrollRef={scrollRef}
					onScroll={measure}
					edgeStart={edge.start}
					tableClassName="min-w-[820px] [&_th]:!text-[12px] [&_th]:!font-medium"
					bodyKey={`${safePage}:${pageSize}`}
					hasRows={pageRows.length > 0}
					isLoading={liveTable.isLoading || dimensionReloading}
					isFetching={liveTable.isFetching}
					emptyState={
						<TableEmptyState
							reduced={reduced}
							message={
								search.trim() ? (
									`No dimensions match “${search.trim()}”.`
								) : (
									<>
										No rows meet the active filter.
										<br />
										Try widening or clearing it.
									</>
								)
							}
							clearLabel={search.trim() ? 'Clear search' : 'Clear filter'}
							onClear={
								search.trim()
									? () => {
											setSearch('')
											setPage(1)
									  }
									: rateFilter !== 'all'
									? () => {
											setRateFilter('all')
											setPage(1)
									  }
									: undefined
							}
						/>
					}
					pagination={{
						page: safePage,
						totalPages,
						onPageChange: setPage,
						pageSize,
						pageSizeOptions: [10, 25, 50],
						onPageSizeChange: (s) => {
							setPageSize(s)
							setPage(1)
						}
					}}
				>
					{flat.map((item, i) => renderItem(item, i))}
				</DataTableV2>
			</div>
		</ReportSection>
	)
}
